import React, { useRef, useState, useMemo, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { useGSAP } from '@gsap/react';
import SmallStepIndicator from './SmallStepIndicator';
import { useTheme } from 'hooks/useTheme';
import useMediaQuery from 'hooks/useMediaQuery';
import { isMobileDevice } from 'utils/helpers';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, ScrollSmoother);

const DURATION_REVEAL = 1;
const ACTIVE_OPACITY = 1;
const INACTIVE_OPACITY = 0.4;

const DESKTOP_IMG_WIDTH = '38.89dvw';
const DESKTOP_IMG_HEIGHT = '39.92dvh';
const MOBILE_IMG_WIDTH = '86.94dvw';
const MOBILE_IMG_HEIGHT = '49.94dvh';

const ShowRooms = ({ showRoomsData, scaleLastImage = true }) => {
  const { isDarkMode } = useTheme();
  const isMobileSize = useMediaQuery();
  const [currentStep, setCurrentStep] = useState(1);
  const mainRef = useRef(null);
  const scrollTriggerInstance = useRef(null);
  const imagesRef = useRef([]);

  const formatNumber = (num) => (num >= 1 && num <= 9 ? `0${num}` : `${num}`);

  const backgroundColors = useMemo(
    () => ({
      0: isDarkMode ? 'rgba(55, 84, 98, 1)' : 'rgba(254, 253, 251, 1)',
      1: isDarkMode ? 'rgba(46, 71, 83, 1)' : 'rgba(249, 247, 242, 1)',
      2: isDarkMode ? 'rgba(36, 56, 66, 1)' : 'rgba(231, 226, 215, 1)',
      3: isDarkMode ? 'rgba(21, 41, 48, 1)' : 'rgba(208, 204, 192, 1)',
      4: isDarkMode ? 'rgba(12, 24, 28, 1)' : 'rgba(185, 182, 172, 1)',
      5: isDarkMode ? 'rgba(6, 13, 15, 1)' : 'rgba(163, 160, 151, 1)',
    }),
    [isDarkMode]
  );

  const lastItem = useMemo(
    () => showRoomsData.items[showRoomsData.items.length - 1],
    [showRoomsData.items]
  );

  useLayoutEffect(() => {
    if (isMobileDevice()) {
      ScrollTrigger.normalizeScroll(true);
      return () => {
        ScrollTrigger.normalizeScroll(false);
      };
    }
  }, []);

  useGSAP(
    () => {
      const items = showRoomsData.items;
      if (items.length === 0 || !mainRef.current) return;

      const pageWrappers = gsap.utils.toArray(
        '.showroom-page-wrapper',
        mainRef.current
      );
      const images = gsap.utils.toArray('.showroom-image', mainRef.current);
      const mainTitleContainers = gsap.utils.toArray(
        '.showroom-main-title',
        mainRef.current
      );
      const stepItems = gsap.utils.toArray(
        '.showroom-step-item',
        mainRef.current
      );
      const dedicatedLastImage = mainRef.current.querySelector(
        '.dedicated-last-image'
      );
      imagesRef.current = images.concat(
        dedicatedLastImage ? [dedicatedLastImage] : []
      );

      items.forEach((_, index) => {
        if (pageWrappers[index])
          gsap.set(pageWrappers[index], {
            zIndex: index,
            clipPath:
              index === 0 ? 'inset(0% 0% 0% 0%)' : 'inset(100% 0% 0% 0%)',
            backgroundColor: backgroundColors[index],
          });
        if (images[index])
          gsap.set(images[index], {
            clipPath:
              index === 0 ? 'inset(0% 0% 0% 0%)' : 'inset(100% 0% 0% 0%)',
          });
        if (mainTitleContainers[index])
          gsap.set(mainTitleContainers[index], {
            clipPath:
              index === 0 ? 'inset(0% 0% 0% 0%)' : 'inset(100% 0% 0% 0%)',
          });
      });

      const mm = gsap.matchMedia(mainRef);

      mm.add(
        { isDesktop: `(min-width: 769px)`, isMobile: `(max-width: 768px)` },
        (context) => {
          let { isMobile } = context.conditions;

          const imgWidth = isMobile ? MOBILE_IMG_WIDTH : DESKTOP_IMG_WIDTH;
          const imgHeight = isMobile ? MOBILE_IMG_HEIGHT : DESKTOP_IMG_HEIGHT;
          const allImages = images.concat(
            dedicatedLastImage ? [dedicatedLastImage] : []
          );

          gsap.set(allImages, {
            width: imgWidth,
            height: imgHeight,
            position: 'absolute',
            top: '50%',
            left: '50%',
            xPercent: -50,
            yPercent: -50,
            x: () => {
              if (isMobile) return 0;
              const parentBounds = mainRef.current.getBoundingClientRect();
              const parentCenter = parentBounds.left + parentBounds.width / 2;
              const viewportCenter = window.innerWidth / 2;
              return viewportCenter - parentCenter;
            },
            transformOrigin: 'center center',
          });

          if (isMobile) {
            const vh = window.innerHeight;
            const imgHeightPx = vh * (parseFloat(MOBILE_IMG_HEIGHT) / 100);
            const yOffset = vh - 52 - imgHeightPx / 2 - vh / 2;
            gsap.set(allImages, { y: yOffset });
          } else {
            gsap.set(allImages, { y: 0 });
          }

          if (dedicatedLastImage) {
            gsap.set(dedicatedLastImage, {
              opacity: 0,
              visibility: 'hidden',
              zIndex: items.length + 10,
              objectFit: 'cover',
            });
          }
          if (items.length === 1 && dedicatedLastImage) {
            gsap.set(images[0], { opacity: 0, visibility: 'hidden' });
            gsap.set(dedicatedLastImage, { opacity: 1, visibility: 'visible' });
          }
          stepItems.forEach((item, index) => {
            const isActive = index === 0;
            item.classList.toggle('active-step', isActive);
            if (isMobile) {
              gsap.set(item, {
                display: isActive ? 'flex' : 'none',
                opacity: 1,
              });
            } else {
              gsap.set(item, {
                opacity: isActive ? ACTIVE_OPACITY : INACTIVE_OPACITY,
                display: 'flex',
              });
            }
          });

          const masterTl = gsap.timeline({
            scrollTrigger: {
              id: 'showRoomsScrub',
              trigger: mainRef.current,
              start: 'top top',
              // --- FIX 1: Add extra scroll distance for the final animation ---
              end: () =>
                `+=${
                  (scaleLastImage ? items.length : items.length - 1) *
                  window.innerHeight
                }`,
              pin: mainRef.current,
              pinType: isMobile ? 'fixed' : 'transform',
              anticipatePin: 1,
              scrub: 1,
              invalidateOnRefresh: true,
              onUpdate: (self) => {
                const totalSegments = scaleLastImage
                  ? items.length
                  : items.length - 1;
                const progress = self.progress * totalSegments;
                const currentSegment = Math.floor(progress);
                const segmentProgress = progress - currentSegment;

                let activeStep =
                  segmentProgress < 0.5 && currentSegment < items.length
                    ? currentSegment
                    : currentSegment + 1;
                activeStep = Math.min(activeStep, items.length - 1);

                if (isMobile) {
                  stepItems.forEach((item, idx) => {
                    if (item) {
                      const isActive = idx === activeStep;
                      gsap.set(item, { display: isActive ? 'flex' : 'none' });
                      item.classList.toggle('active-step', isActive);
                    }
                  });
                } else {
                  stepItems.forEach((item, idx) => {
                    if (item) {
                      let newOpacity = INACTIVE_OPACITY;
                      if (idx === currentSegment) {
                        newOpacity = gsap.utils.interpolate(
                          ACTIVE_OPACITY,
                          INACTIVE_OPACITY,
                          segmentProgress
                        );
                      } else if (
                        idx === currentSegment + 1 &&
                        currentSegment < items.length - 1
                      ) {
                        newOpacity = gsap.utils.interpolate(
                          INACTIVE_OPACITY,
                          ACTIVE_OPACITY,
                          segmentProgress
                        );
                      }
                      if (
                        idx === items.length - 1 &&
                        currentSegment >= items.length - 1
                      ) {
                        newOpacity = ACTIVE_OPACITY;
                      }
                      gsap.set(item, {
                        opacity: newOpacity,
                        overwrite: 'auto',
                      });
                      item.classList.toggle('active-step', idx === activeStep);
                    }
                  });
                }
                setCurrentStep(Math.min(activeStep + 1, items.length));
              },
            },
          });

          for (let i = 1; i < items.length; i++) {
            const revealStartTime = (i - 1) * DURATION_REVEAL;
            if (pageWrappers[i])
              masterTl.to(
                pageWrappers[i],
                {
                  clipPath: 'inset(0% 0% 0% 0%)',
                  duration: DURATION_REVEAL,
                  ease: 'none',
                },
                revealStartTime
              );
            if (images[i])
              masterTl.to(
                images[i],
                {
                  clipPath: 'inset(0% 0% 0% 0%)',
                  duration: DURATION_REVEAL,
                  ease: 'none',
                },
                revealStartTime
              );
            if (mainTitleContainers[i])
              masterTl.to(
                mainTitleContainers[i],
                {
                  clipPath: 'inset(0% 0% 0% 0%)',
                  duration: DURATION_REVEAL,
                  ease: 'none',
                },
                revealStartTime
              );
          }

          if (
            dedicatedLastImage &&
            lastItem &&
            items.length > 0 &&
            scaleLastImage
          ) {
            const lastItemIndex = items.length - 1;
            const originalLastImageInPage = images[lastItemIndex];
            const finalImageAnimStartTime = lastItemIndex * DURATION_REVEAL;

            if (originalLastImageInPage)
              masterTl.set(
                originalLastImageInPage,
                { opacity: 0, visibility: 'hidden' },
                finalImageAnimStartTime
              );
            masterTl.set(
              dedicatedLastImage,
              { opacity: 1, visibility: 'visible' },
              finalImageAnimStartTime
            );

            if (isMobile) {
              const vh = window.innerHeight;
              const imgHeightPx = vh * (parseFloat(MOBILE_IMG_HEIGHT) / 100);
              const preciseScale = vh / imgHeightPx;
              masterTl.to(
                dedicatedLastImage,
                {
                  scale: preciseScale,
                  y: 0,
                  duration: DURATION_REVEAL,
                  ease: 'power2.inOut',
                },
                finalImageAnimStartTime
              );
            } else {
              const scaleToCover = Math.max(
                100 / parseFloat(DESKTOP_IMG_WIDTH),
                100 / parseFloat(DESKTOP_IMG_HEIGHT)
              );
              masterTl.to(
                dedicatedLastImage,
                {
                  scale: scaleToCover,
                  duration: DURATION_REVEAL,
                  ease: 'power2.inOut',
                },
                finalImageAnimStartTime
              );
            }
          }

          scrollTriggerInstance.current = masterTl.scrollTrigger;
        }
      );

      return () => {
        mm.revert();
      };
    },
    {
      scope: mainRef,
      dependencies: [showRoomsData, backgroundColors, scaleLastImage],
      revertOnUpdate: true,
    }
  );

  useLayoutEffect(() => {
    if (imagesRef.current.length === 0 || !showRoomsData?.items) return;
    const allImageData = showRoomsData.items.concat(
      lastItem && scaleLastImage ? [lastItem] : []
    );
    imagesRef.current.forEach((img, i) => {
      const item = allImageData[i];
      if (img && item) {
        img.src = isDarkMode
          ? item.darkModeImageUrl || item.lightModeImageUrl
          : item.lightModeImageUrl;
      }
    });
  }, [isDarkMode, lastItem, scaleLastImage, showRoomsData]);

  const handleStepClick = (targetStepIndex) => {
    const mainST = scrollTriggerInstance.current;
    const smoother = ScrollSmoother.get();
    if (!smoother || !mainST) return;
    const target0IndexedStep = targetStepIndex - 1;
    const totalSteps = showRoomsData.items.length;
    if (currentStep - 1 === target0IndexedStep || totalSteps <= 1) {
      return;
    }

    const totalAnimSegments = scaleLastImage ? totalSteps : totalSteps - 1;
    if (totalAnimSegments === 0) return;
    const targetProgress = target0IndexedStep / totalAnimSegments;
    const targetScrollY =
      mainST.start + (mainST.end - mainST.start) * targetProgress;

    smoother.scrollTo(targetScrollY, { duration: 1, ease: 'power2.inOut' });
  };

  return (
    <div
      className={`${
        isDarkMode ? '' : 'dark-menu-section'
      } show-rooms-container slider-bg-second-colors full-pager relative`}
      ref={mainRef}
    >
      <div className='absolute full-height full-width flex'>
        {showRoomsData.items.map((item, index) => (
          <div
            key={`${item.title}-${index}-bg-wrapper`}
            className='showroom-page-wrapper page-wrapper absolute full-height full-width'
            style={{ backgroundColor: backgroundColors[index] }}
          >
            <img
              src={
                isDarkMode
                  ? item?.darkModeImageUrl || item?.lightModeImageUrl
                  : item.lightModeImageUrl
              }
              alt={item.title}
              className='showroom-image absolute'
            />
            {!isMobileSize && (
              <div className='showroom-main-title main-title-container absolute page-wrapper-y bottom-0 flex'>
                <span className='font-size-120 uppercase font-secondary'>
                  {formatNumber(index + 1)}
                </span>
                <p className='font-size-120 uppercase'>{item.mainTitle}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {lastItem && scaleLastImage && (
        <img
          src={
            isDarkMode
              ? lastItem?.darkModeImageUrl || lastItem?.lightModeImageUrl
              : lastItem?.lightModeImageUrl
          }
          alt={lastItem.title}
          className='dedicated-last-image absolute'
        />
      )}
      <div
        className={`absolute full-height full-width page-wrapper ${
          !isMobileSize ? 'show-rooms-container-desktop' : ''
        }`}
        style={{ zIndex: showRoomsData.items?.length || 1 }}
      >
        <div className='relative full-height full-width'>
          <div className='flex full-width justify-between show-rooms-header'>
            <div className='client-steps flex flex-column'>
              {showRoomsData.items?.length > 0 &&
                showRoomsData.items.map((item, index) => (
                  <div
                    key={`${item.title}-${index}-step`}
                    className='showroom-step-item flex client-step-item column-gap-24'
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleStepClick(index + 1)}
                  >
                    <span className='index-step'>
                      {formatNumber(index + 1)}
                    </span>
                    <span className='no-wrap step-title'>{item.title}</span>
                  </div>
                ))}
              {isMobileSize && (
                <p className='show-rooms-description word-break-all'>
                  {showRoomsData.description}
                </p>
              )}
            </div>
            {!isMobileSize && (
              <p className='show-rooms-description'>
                {showRoomsData.description}
              </p>
            )}
            {showRoomsData.items.length > 0 && (
              <SmallStepIndicator
                totalSteps={showRoomsData.items.length}
                currentStep={currentStep}
                setCurrentStep={handleStepClick}
                className={`show-rooms-step-indicator ${
                  isMobileSize ? '' : 'absolute bottom-0 right-0'
                }`}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShowRooms;
